import { hasPendingImageAlternative, isNoReferrerHost, normalizeCoverUrl } from './img-fix';
import { getCachedSlug, setCachedSlug } from './pretty-cache';
import { getAuthHint } from './auth';
import { chapterUrlSuffix } from './chapter-volume';
import { prettyWorkPath, HIDDEN_PLUGIN_ID } from './pretty-path';

const PALETTE: Array<[string, string]> = [
  ['#5b3a52', '#2c1b2b'],
  ['#324a63', '#131c29'],
  ['#c9a24a', '#5a3d1c'],
  ['#1b2a3a', '#0a1018'],
  ['#7a6a2a', '#2a2410'],
  ['#3a6a9a', '#132436'],
  ['#7a3a3a', '#2a1212'],
  ['#8a3a6a', '#2a1222'],
  ['#3a7a5a', '#12261c'],
  ['#4a3a7a', '#1a1229'],
];

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

export function coverGradient(seed: string): [string, string] {
  return PALETTE[hashStr(seed) % PALETTE.length];
}

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

const LIBRARY_ICON = '<path d="M4 4h4v16H4zM11 4h4v16h-4zM18 5l3 1-4 15-3-1z"/>';

/**
 * Рендерить обкладинку: якщо є посилання — <img> з плавною появою поверх
 * яскравішого за фон прямокутника-плейсхолдера, інакше — градієнтний
 * плейсхолдер з ініціалами. Якщо `inLibrary` === true, поверх обкладинки
 * додається затемнення та значок бібліотеки в кутку.
 */
export function coverEl(name: string, cover: string | undefined, seed = name, inLibrary = false): HTMLElement {
  const div = document.createElement('div');
  div.className = 'cardcover';
  if (cover) {
    div.classList.add('cover-loading');
    const img = document.createElement('img');
    img.loading = 'lazy';
    img.alt = name;
    img.addEventListener('load', () => {
      div.classList.remove('cover-loading');
      div.classList.add('cover-loaded');
    });
    img.addEventListener('error', () => {
      // Глобальний обробник (img-fix.ts) уже пробує запасну адресу (напр. виправлену
      // кирилицю в імені файлу) — поки вона не провалиться, картинку не прибираємо.
      if (hasPendingImageAlternative(img)) return;
      img.remove();
      div.classList.remove('cover-loading');
      applyGradient();
    });
    const coverSrc = normalizeCoverUrl(cover);
    if (isNoReferrerHost(coverSrc)) img.referrerPolicy = 'no-referrer';
    img.src = coverSrc;
    div.appendChild(img);
  } else {
    applyGradient();
  }
  function applyGradient() {
    const [from, to] = coverGradient(seed);
    div.style.background = `linear-gradient(160deg, ${from}, ${to})`;
    div.textContent = initials(name);
  }
  if (inLibrary) {
    div.classList.add('in-library');
    const overlay = document.createElement('div');
    overlay.className = 'cover-lib-overlay';
    const badge = document.createElement('div');
    badge.className = 'cover-lib-badge';
    badge.setAttribute('aria-label', 'У бібліотеці');
    badge.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor">${LIBRARY_ICON}</svg>`;
    div.appendChild(overlay);
    div.appendChild(badge);
  }
  return div;
}

/**
 * Гарантує РІВНО 2 видимих рядки для елемента з довгою назвою: якщо повний
 * текст не влазить у поточну (CSS-обмежену за max-height) висоту елемента —
 * обрізає посимвольно (двійковий пошук за scrollHeight) і дописує "…".
 *
 * Робимо це JS-виміром, а не покладаємось лише на CSS `-webkit-line-clamp`:
 * у частині браузерів/збірок цей хак рахує кількість рядків "на око" і
 * інколи пропускає частину 3-го рядка, попри виставлені max-height та
 * overflow:hidden (саме так і виглядала помилка — "Безтурботне / життя… /
 * всесильної", де "…" з'являлось ще на 2-му рядку, а 3-й все одно рендерився).
 *
 * ВАЖЛИВО: викликати ПІСЛЯ того, як el вже вставлено в живий (прикріплений
 * до document) DOM — інакше scrollHeight/clientHeight будуть 0 і функція
 * нічого не зробить.
 */
export function clampTwoLines(el: HTMLElement): void {
  const full = el.textContent || '';
  if (!full) return;
  if (el.clientHeight === 0) return; // елемент ще не має розкладки — пропускаємо, а не ламаємо текст
  if (el.scrollHeight <= el.clientHeight + 1) return; // і так влазить — CSS clamp впорався сам

  let lo = 0;
  let hi = full.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    el.textContent = full.slice(0, mid).trimEnd() + '…';
    if (el.scrollHeight <= el.clientHeight + 1) lo = mid;
    else hi = mid - 1;
  }
  el.textContent = (lo > 0 ? full.slice(0, lo).trimEnd() : full.slice(0, 1)) + '…';
}

export function fmtDateGroup(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diffDays === 0) return 'Сьогодні';
  if (diffDays === 1) return 'Вчора';
  return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
}

/** Українська плюралізація: pluralUk(5, ['хвилина','хвилини','хвилин']) -> 'хвилин'. */
export function pluralUk(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return forms[1];
  return forms[2];
}

/** Відносний час у форматі "N годин тому" / "щойно" тощо. */
export function fmtRelative(iso: string): string {
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 45) return 'щойно';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} ${pluralUk(diffMin, ['хвилину', 'хвилини', 'хвилин'])} тому`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} ${pluralUk(diffHour, ['годину', 'години', 'годин'])} тому`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 30) return `${diffDay} ${pluralUk(diffDay, ['день', 'дні', 'днів'])} тому`;
  return new Date(iso).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** Дані, які сервер підставляє в сторінку при відкритті короткого посилання
 * /<джерело>/<слаг>[/chapterN] (routes/pretty.ts) — замість ?plugin=&path=... */
declare global {
  interface Window {
    __PRETTY__?: Record<string, string | number>;
  }
}

export function qs(name: string): string | null {
  const v = new URLSearchParams(location.search).get(name);
  if (v !== null) return v;
  const pretty = window.__PRETTY__;
  if (pretty && name in pretty) return String(pretty[name]);
  return null;
}

/** Слаг короткого посилання поточної сторінки твору, якщо вона була
 * відкрита через /<джерело>/<слаг> (інакше — undefined, слаг ще треба
 * створити/дізнатись через /api/plugins/:id/slug). */
export function prettySlug(): string | undefined {
  const v = window.__PRETTY__?.slug;
  return v !== undefined ? String(v) : undefined;
}

/** Ключі query-рядка, які "переїжджають" у сам шлях короткого посилання
 * (plugin/path/...) чи в дані сторінки й далі з URL не потрібні — щоб не
 * тягнути їх дублем в query поруч із коротким посиланням. */
const PRETTY_ABSORBED_KEYS = ['plugin', 'path', 'novelPath', 'name', 'num', 'cover'];

function remainingQuery(): string {
  const params = new URLSearchParams(location.search);
  for (const k of PRETTY_ABSORBED_KEYS) params.delete(k);
  const s = params.toString();
  return s ? `?${s}` : '';
}

function buildPrettyUrl(pluginId: string, slug: string, chapterNumber?: number): string {
  const suffix = chapterNumber !== undefined ? chapterUrlSuffix(chapterNumber) : '';
  return `${prettyWorkPath(pluginId, slug, suffix)}${remainingQuery()}`;
}

/** Синхронно, без жодного запиту: якщо слаг для (pluginId, novelPath) уже
 * відомий (з локального кешу або з того, що сторінку й так відкрито за
 * коротким посиланням), одразу підміняє адресу в рядку браузера — щоб не
 * встиг майнути "старий" вигляд URL. Викликати як МОЖНА раніше, до
 * будь-яких await у сторінці. chapterNumber — те саме ChapterItem.chapterNumber
 * (не позиція в списку!), decodeChapterNumber() дістане з нього том/розділ.
 * Повертає true, якщо адресу вже підмінено (і повторно звертатись до
 * сервера за реєстрацією не треба). */
export function switchToPrettyUrlSync(pluginId: string, novelPath: string, chapterNumber?: number): boolean {
  const slug = prettySlug() ?? getCachedSlug(pluginId, novelPath);
  if (!slug) return false;
  setCachedSlug(pluginId, novelPath, slug);
  const prettyUrl = buildPrettyUrl(pluginId, slug, chapterNumber);
  if (location.pathname + location.search !== prettyUrl) history.replaceState(null, '', prettyUrl);
  return true;
}

/** Отримує (і за потреби створює на сервері) слаг для твору й підміняє
 * адресу в рядку браузера на коротке посилання /<pluginId>/<слаг> —
 * і кешує його локально на майбутнє (щоб наступного разу спрацював
 * switchToPrettyUrlSync() без затримки). Помилки мовчки ігноруються —
 * сторінка й так повністю працездатна зі старою адресою. Спершу варто
 * викликати switchToPrettyUrlSync(); цю функцію — лише якщо вона
 * повернула false. */
export async function switchToPrettyUrl(
  pluginId: string,
  novelPath: string,
  title: string,
  chapterNumber?: number,
): Promise<void> {
  if (switchToPrettyUrlSync(pluginId, novelPath, chapterNumber)) return;
  try {
    const res = await fetch(
      `/api/plugins/${encodeURIComponent(pluginId)}/slug?path=${encodeURIComponent(novelPath)}&title=${encodeURIComponent(title)}`,
    );
    if (!res.ok) return;
    const slug = (await res.json()).slug;
    if (!slug) return;
    setCachedSlug(pluginId, novelPath, slug);
    const prettyUrl = buildPrettyUrl(pluginId, slug, chapterNumber);
    if (location.pathname + location.search !== prettyUrl) history.replaceState(null, '', prettyUrl);
  } catch {
    /* сторінка лишається на поточній (query-)адресі — не критично */
  }
}

/**
 * Посилання для кнопки \"Поділитися\": коротке /<джерело>/<слаг>[/chapterN] без службового
 * query (from/tab/progress тощо — це стан навігації саме цього користувача), на поточному
 * домені. На відміну від location.href, не залежить від того, чи встигла адреса вже
 * підмінитись на коротку (фоновий switchToPrettyUrl міг ще не відпрацювати або впасти):
 * слаг беремо з сторінки/кешу, а якщо його немає — запитуємо й чекаємо. Якщо слаг
 * отримати не вдалось — повертаємо location.href (поточну адресу як є).
 */
export async function getShareUrl(
  pluginId: string | null | undefined,
  novelPath: string | null | undefined,
  title: string,
  chapterNumber?: number,
): Promise<string> {
  if (!pluginId || !novelPath) return location.href;
  try {
    let slug = prettySlug() ?? getCachedSlug(pluginId, novelPath);
    if (!slug) {
      const res = await fetch(
        `/api/plugins/${encodeURIComponent(pluginId)}/slug?path=${encodeURIComponent(novelPath)}&title=${encodeURIComponent(title || document.title)}`,
      );
      if (res.ok) slug = (await res.json()).slug;
    }
    if (!slug) return location.href;
    setCachedSlug(pluginId, novelPath, slug);
    const suffix = chapterNumber !== undefined && !Number.isNaN(chapterNumber) ? chapterUrlSuffix(chapterNumber) : '';
    return `${location.origin}${prettyWorkPath(pluginId, slug, suffix)}`;
  } catch {
    return location.href;
  }
}

export function toast(message: string) {
  let el = document.getElementById('toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'toast';
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.classList.add('show');
  window.clearTimeout((el as any)._t);
  (el as any)._t = window.setTimeout(() => el!.classList.remove('show'), 2200);
}

export interface ConfirmDialogOptions {
  title?: string;
  /** Пояснювальний текст, що виводиться над кнопками підтвердження. */
  details?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /** Підсвітити кнопку підтвердження як небезпечну дію. */
  danger?: boolean;
}

/**
 * Власна (у стилі застосунку) заміна window.confirm(). Показує центровану
 * модалку поверх робочої зони й повертає true/false залежно від вибору.
 */
export function confirmDialog(opts: ConfirmDialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay-center';

    const box = document.createElement('div');
    box.className = 'center-modal';

    if (opts.title) {
      const h = document.createElement('h3');
      h.className = 'modal-title';
      h.textContent = opts.title;
      box.appendChild(h);
    }
    if (opts.details) {
      const d = document.createElement('p');
      d.className = 'modal-details';
      d.textContent = opts.details;
      box.appendChild(d);
    }
    const msg = document.createElement('p');
    msg.className = 'modal-message';
    msg.textContent = opts.message;
    box.appendChild(msg);

    const actions = document.createElement('div');
    actions.className = 'modal-actions';
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn';
    cancelBtn.textContent = opts.cancelText || 'Скасувати';
    const okBtn = document.createElement('button');
    okBtn.type = 'button';
    okBtn.className = opts.danger ? 'btn btn-danger' : 'btn btn-accent';
    okBtn.textContent = opts.confirmText || 'Гаразд';
    actions.appendChild(cancelBtn);
    actions.appendChild(okBtn);
    box.appendChild(actions);

    overlay.appendChild(box);
    document.body.appendChild(overlay);

    const close = (result: boolean) => {
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve(result);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    };
    document.addEventListener('keydown', onKey);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close(false);
    });
    cancelBtn.addEventListener('click', () => close(false));
    okBtn.addEventListener('click', () => close(true));
    okBtn.focus();
  });
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ---------------------------------------------------------------------------
// Маршрутизація на сторінку твору залежно від contentType джерела: anime йде
// на окрему anime.html (власний UX — вибір перекладу, плеєр серій, без
// сторінки-читалки взагалі), novel/manga — на novel.html, як і раніше.

let contentTypeCachePromise: Promise<Record<string, 'novel' | 'manga' | 'anime'>> | null = null;

/** Один раз за сесію сторінки завантажує {pluginId: contentType} з /api/plugins
 * і далі повертає закешовану мапу (усі виклики поділяють той самий проміс). */
export async function pluginContentTypes(): Promise<Record<string, 'novel' | 'manga' | 'anime'>> {
  if (!contentTypeCachePromise) {
    contentTypeCachePromise = fetch('/api/plugins')
      .then((r) => (r.ok ? r.json() : []))
      .then((list: { id: string; contentType: 'novel' | 'manga' | 'anime' }[]) =>
        Object.fromEntries(list.map((p) => [p.id, p.contentType])),
      )
      .catch(() => ({}));
  }
  return contentTypeCachePromise;
}

/** Синхронний білдер посилання на сторінку твору — /<pluginId>/<слаг>, якщо
 * слаг уже є в локальному кеші (client/src/pretty-cache.ts), інакше —
 * робочий "старий" формат (сервер і так підмінить адресу після завантаження
 * сторінки, а заразом і кеш поповниться на наступний раз). */
export function workHref(
  pluginId: string,
  novelPath: string,
  contentType: 'novel' | 'manga' | 'anime' | undefined,
  extraQuery = '',
): string {
  const slug = getCachedSlug(pluginId, novelPath);
  if (slug) {
    const qsExtra = extraQuery.startsWith('&') ? `?${extraQuery.slice(1)}` : extraQuery;
    return `${prettyWorkPath(pluginId, slug)}${qsExtra}`;
  }
  const base = contentType === 'anime' ? '/anime' : '/novel';
  return `${base}?plugin=${encodeURIComponent(pluginId)}&path=${encodeURIComponent(novelPath)}${extraQuery}`;
}

/** Оновлює href уже намальованих посилань на твір, коли клієнтський кеш
 * слагів прогрівся (warmSlugCache) вже ПІСЛЯ першої відмальовки списку —
 * без цього спрацював би лише "старий" workHref(), з яким вони й
 * створювались. Елементи мають нести дані в data-pretty-*, див. приклади
 * використання в browse.ts/library.ts/updates.ts/history.ts. */
export function refreshPrettyHrefs(container: ParentNode): void {
  container.querySelectorAll<HTMLAnchorElement>('a[data-pretty-plugin][data-pretty-path]').forEach((a) => {
    const pluginId = a.dataset.prettyPlugin!;
    const novelPath = a.dataset.prettyPath!;
    const contentType = a.dataset.prettyType as 'novel' | 'manga' | 'anime' | undefined;
    const extraQuery = a.dataset.prettyExtra ?? '';
    a.href = workHref(pluginId, novelPath, contentType, extraQuery);
  });
}

/** Посилання на сторінку "Огляд" з одразу вибраним джерелом: /plugin/<id> замість /browse.html?plugin=<id>. */
export function sourceHref(pluginId: string): string {
  // Гостю (без акаунта) каталог єдиного джерела живе на головній — /plugin/<id> йому не показуємо.
  if (pluginId === HIDDEN_PLUGIN_ID && getAuthHint() === 'guest') return '/';
  return `/plugin/${encodeURIComponent(pluginId)}`;
}
